import React from "react";
import { motion } from "framer-motion";
import { EALogo } from "@/components/brand/EALogo";

const Line = ({ children, delay = 0 }) => (
  <span className="line-mask">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ delay, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      className="inline-block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden bg-ink-950 grid-bg"
      data-testid="hero-section"
    >
      {/* Aurora spectrum */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="aurora-blob animate-aurora"
          style={{ background: "#00F0FF", width: 620, height: 620, top: "-8%", left: "18%" }}
        />
        <div
          className="aurora-blob animate-aurora-slow"
          style={{ background: "#8A2BE2", width: 720, height: 720, bottom: "-14%", right: "10%" }}
        />
        <div
          className="aurora-blob animate-aurora"
          style={{ background: "#39FF14", width: 480, height: 480, top: "40%", left: "50%", opacity: 0.4 }}
        />
      </div>

      {/* Grid overlay lines */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-y-0 left-[12%] w-px bg-white/[0.04]" />
        <div className="absolute inset-y-0 right-[12%] w-px bg-white/[0.04]" />
        <div className="absolute inset-x-0 top-[18%] h-px bg-white/[0.04]" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 pt-32 pb-24 md:pt-40 md:pb-32 min-h-[100svh] flex flex-col justify-between">
        {/* Top mono meta */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 font-mono text-[10px] tracking-[0.3em] uppercase text-white/40"
          data-testid="hero-meta"
        >
          <div>
            <div className="text-spectrum-green mb-1">◈ Prospectus 01</div>
            <div>Stockholm — Global</div>
          </div>
          <div>
            <div className="text-spectrum-green mb-1">◈ Instrument</div>
            <div>Guarantee Convertible Bond</div>
          </div>
          <div>
            <div className="text-spectrum-green mb-1">◈ Stack</div>
            <div>Merkle · IoT · ERP</div>
          </div>
          <div>
            <div className="text-spectrum-green mb-1">◈ Vintage</div>
            <div>{new Date().getFullYear()} — ∞</div>
          </div>
        </motion.div>

        {/* Center headline block with EA mark aurora */}
        <div className="flex-1 flex items-center py-16">
          <div className="w-full">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-6 flex items-center gap-4 font-mono text-[11px] tracking-[0.3em] uppercase text-white/50"
            >
              <span className="text-spectrum-green">◢◤</span>
              <span>A software IP holding · financed by guarantee-backed convertible bonds</span>
            </motion.div>

            <h1
              className="font-display font-black text-white leading-[0.88] tracking-tighter text-[13vw] md:text-[9.5vw] lg:text-[8.5vw]"
              data-testid="hero-headline"
            >
              <Line delay={0.4}>Financing the</Line>
              <Line delay={0.55}>
                <span className="spectrum-text">automated</span>
              </Line>
              <Line delay={0.7}>ledger economy.</Line>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="mt-10 max-w-xl text-white/60 text-base md:text-lg font-light leading-relaxed"
              data-testid="hero-subline"
            >
              We build Merkle-tree ledgers, IoT-driven inter-ERP commerce, and audit
              automation. Funded by convertible bonds. Guaranteed by the code we ship.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.7, duration: 0.8 }}
              className="mt-12 flex flex-wrap items-center gap-4"
            >
              <a
                href="#bond"
                className="group relative inline-flex items-center gap-3 bg-spectrum-green px-6 py-4 font-mono text-[11px] tracking-[0.25em] uppercase text-ink-950 hover:bg-white transition-colors"
                data-testid="hero-cta-invest"
              >
                Register investor interest
                <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
              </a>
              <a
                href="#portfolio"
                className="inline-flex items-center gap-3 border border-white/15 px-6 py-4 font-mono text-[11px] tracking-[0.25em] uppercase text-white/80 hover:border-white hover:text-white transition-colors"
                data-testid="hero-cta-portfolio"
              >
                See the portfolio
              </a>
            </motion.div>
          </div>
        </div>

        {/* Bottom row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 0.8 }}
          className="border-t border-white/10 pt-6 flex flex-wrap items-end justify-between gap-6 font-mono text-[10px] tracking-[0.3em] uppercase text-white/40"
          data-testid="hero-footer"
        >
          <div className="flex items-center gap-3">
            <span className="text-spectrum-green"><EALogo size={22} strokeWidth={8} /></span>
            <span>Chapter 00 — Genesis</span>
          </div>
          <div className="hidden md:block">Scroll ↓ for the thesis</div>
          <div className="text-white/60">erik@svenskainvest.com</div>
        </motion.div>
      </div>
    </section>
  );
};
